
/**
 * Recovery Utils
 * 
 * Utilities for managing application recovery mode
 * This helps the app recover from severe errors or storage issues
 */

// Flag for recovery mode
const RECOVERY_MODE_KEY = 'deployment_recovery_mode';
const RECOVERY_TIMESTAMP_KEY = 'deployment_recovery_timestamp';
const DEPLOYMENT_RECOVERY_KEY = 'deployment_recovery_info';

/**
 * Checks if the application is in recovery mode
 */
export const isInRecoveryMode = (): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    
    // Check for window flag first (faster than localStorage)
    if (window._deploymentRecoveryMode) return true;
    
    // Check localStorage
    return localStorage.getItem(RECOVERY_MODE_KEY) === 'true';
  } catch (e) {
    // If we can't read localStorage, we're not in recovery mode
    return false;
  }
};

/**
 * Get saved deployment recovery information
 * Useful when trying to recover from a failed deployment
 */
export const getDeploymentRecoveryInfo = (): object | null => {
  try {
    if (typeof window === 'undefined') return null;
    
    const recoveryInfo = localStorage.getItem(DEPLOYMENT_RECOVERY_KEY);
    if (!recoveryInfo) return null;
    
    return JSON.parse(recoveryInfo);
  } catch (e) {
    console.error('Failed to get recovery info:', e);
    return null;
  }
};

/**
 * Store deployment recovery information
 * @param info The deployment information to store for recovery
 */
export const storeDeploymentRecoveryInfo = (info: object): boolean => {
  try {
    if (typeof window === 'undefined') return false;
    
    localStorage.setItem(DEPLOYMENT_RECOVERY_KEY, JSON.stringify(info));
    return true;
  } catch (e) {
    console.error('Failed to store recovery info:', e);
    return false;
  }
};

/**
 * Enter recovery mode
 * Reduces feature set and clears problematic storage
 */
export const enterRecoveryMode = (): void => {
  try {
    if (isInRecoveryMode()) return; // Already in recovery mode
    
    console.warn('⚠️ Entering recovery mode');
    
    // Set recovery flags
    window._deploymentRecoveryMode = true;
    localStorage.setItem(RECOVERY_MODE_KEY, 'true');
    localStorage.setItem(RECOVERY_TIMESTAMP_KEY, Date.now().toString());
    
    // Clear session storage (often contains temporary state that could be corrupted)
    sessionStorage.clear();
    
    // Store the current URL path for potential redirection after recovery
    try {
      localStorage.setItem('recovery_path', window.location.pathname);
    } catch (e) {
      // Silent fail - not critical
    }
    
    // Disable some features that might be causing issues
    // This is handled by the components checking isInRecoveryMode()
  } catch (e) {
    console.error('Failed to enter recovery mode:', e);
  }
};

/**
 * Exit recovery mode
 */
export const exitRecoveryMode = (): void => {
  try {
    if (!isInRecoveryMode()) return; // Not in recovery mode
    
    console.log('✅ Exiting recovery mode');
    
    // Clear recovery flags
    window._deploymentRecoveryMode = false;
    localStorage.removeItem(RECOVERY_MODE_KEY);
    localStorage.removeItem(RECOVERY_TIMESTAMP_KEY);
    
    // Record exit time
    try {
      localStorage.setItem('recovery_exit_timestamp', Date.now().toString());
    } catch (e) {
      // Silent fail - not critical
    }
  } catch (e) {
    console.error('Failed to exit recovery mode:', e);
  }
};

/**
 * Check if it's safe to exit recovery mode
 */
export const canExitRecoveryMode = (): boolean => {
  try {
    if (!isInRecoveryMode()) return false; // Not in recovery mode
    
    // Check when recovery mode was activated
    const recoveryTimestamp = localStorage.getItem(RECOVERY_TIMESTAMP_KEY);
    if (!recoveryTimestamp) return true; // No timestamp, assume it's safe to exit
    
    const recoveryTime = parseInt(recoveryTimestamp, 10);
    const now = Date.now();
    
    // Minimum time in recovery mode (5 minutes)
    const minRecoveryTime = 5 * 60 * 1000;
    
    return (now - recoveryTime) > minRecoveryTime;
  } catch (e) {
    console.error('Failed to check if can exit recovery mode:', e);
    return false;
  }
};

/**
 * Fast recovery - clears non-essential storage while preserving critical data
 * @returns Number of items removed
 */
export const fastRecover = (): number => {
  try {
    let removedCount = 0;
    
    // List of prefixes for non-essential items to clear
    const nonEssentialPrefixes = [
      'cache_', 
      'temp_', 
      'log_', 
      'debug_',
      'metrics_',
      'stats_',
      'render_',
      'perf_'
    ];
    
    // Clear all items with non-essential prefixes
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      
      // Check if this is a non-essential item
      if (nonEssentialPrefixes.some(prefix => key.startsWith(prefix))) {
        try {
          localStorage.removeItem(key);
          removedCount++;
        } catch (e) {
          // Continue with other items
        }
      }
    }
    
    // Also clear session storage completely
    const sessionLength = sessionStorage.length;
    sessionStorage.clear();
    removedCount += sessionLength;
    
    return removedCount;
  } catch (e) {
    console.error('Fast recovery failed:', e);
    return 0;
  }
};

// Setup window type augmentation for TypeScript
declare global {
  interface Window {
    _deploymentRecoveryMode?: boolean;
  }
}

export default {
  isInRecoveryMode,
  enterRecoveryMode,
  exitRecoveryMode,
  canExitRecoveryMode,
  fastRecover,
  getDeploymentRecoveryInfo,
  storeDeploymentRecoveryInfo
};
